import { z } from 'zod';
import type { AgentScheduleResponse } from '@erp/rpc';
import { validateCronExpression } from 'src/components/common/CronInput';

/** Trims and accepts only IANA names `Intl` can read — same bar as the server. */
export const AgentScheduleTimezoneSchema = z
  .string()
  .trim()
  .min(1)
  .refine((timezone) => {
    try {
      new Intl.DateTimeFormat('en-US', { timeZone: timezone });
      return true;
    } catch {
      return false;
    }
  });

/** Form state for the agent schedule dialog; `id` is set when editing. */
export interface AgentScheduleDraft {
  id: string | null;
  description: string;
  cronExpression: string;
  timezone: string;
  isEnabled: boolean;
}

/** Weekdays at 9:00 — a sensible starting point for a business agent. */
export const DEFAULT_AGENT_SCHEDULE_CRON = '0 9 * * 1-5';

/**
 * New schedules default to the viewer's IANA zone (the browser clock).
 * Org timezone, then UTC, cover environments where that name is missing
 * or not a real zone.
 */
export function defaultAgentScheduleTimezone(
  orgTimezone?: string | null,
  viewerTimezone: string = Intl.DateTimeFormat().resolvedOptions().timeZone,
): string {
  const viewer = AgentScheduleTimezoneSchema.safeParse(viewerTimezone);
  if (viewer.success) return viewer.data;
  const org = AgentScheduleTimezoneSchema.safeParse(orgTimezone ?? '');
  if (org.success) return org.data;
  return 'UTC';
}

export function newAgentScheduleDraft(timezone: string): AgentScheduleDraft {
  return {
    id: null,
    description: '',
    cronExpression: DEFAULT_AGENT_SCHEDULE_CRON,
    timezone,
    isEnabled: true,
  };
}

export function agentScheduleToDraft(
  schedule: AgentScheduleResponse,
): AgentScheduleDraft {
  return {
    id: schedule.id,
    description: schedule.description ?? '',
    cronExpression: schedule.cronExpression,
    timezone: schedule.timezone,
    isEnabled: schedule.isEnabled,
  };
}

export function isAgentScheduleDraftDirty(
  draft: AgentScheduleDraft,
  initial: AgentScheduleDraft,
): boolean {
  return (
    draft.description !== initial.description ||
    draft.cronExpression !== initial.cronExpression ||
    draft.timezone !== initial.timezone ||
    draft.isEnabled !== initial.isEnabled
  );
}

export function isAgentScheduleDraftValid(draft: AgentScheduleDraft): boolean {
  return (
    AgentScheduleTimezoneSchema.safeParse(draft.timezone).success &&
    validateCronExpression(draft.cronExpression).isValid
  );
}

/** The write payload shared by create and update, from a draft. */
export function agentScheduleDraftToInput(draft: AgentScheduleDraft) {
  const description = draft.description.trim();
  const timezone = AgentScheduleTimezoneSchema.safeParse(draft.timezone);
  return {
    description: description.length > 0 ? description : null,
    cronExpression: draft.cronExpression.trim(),
    timezone: timezone.success ? timezone.data : draft.timezone.trim(),
    isEnabled: draft.isEnabled,
  };
}
